import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseDataGridComponent } from '@components/base-data-grid.component';
import { dateFormattingOptions } from '@domain/formatting';
import { ByProductItem } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { ProductsService } from '@services/products.service';
import { ColDef, RowClickedEvent, themeAlpine, GridApi } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';
import { PageHeaderComponent } from '@components/page-header/page-header.component';

@Component({
  selector: 'md-pricing-byproducts-page',
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, AgGridAngular, ReactiveFormsModule, TranslateModule],
  templateUrl: './pricing-byproducts-page.component.html',
  styleUrl: './pricing-byproducts-page.component.scss',
  standalone: true
})
export class PricingByproductsPageComponent extends BaseDataGridComponent implements OnDestroy {
  private productsService = inject(ProductsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  private gridApi = signal<GridApi<ByProductItem> | null>(null);
  isLoading = signal<boolean>(false);
  isExporting = signal(false);
  allByproducts = signal<ByProductItem[]>([]);
  displayByproducts = signal<ByProductItem[]>([]);

  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<ByProductItem>;
  theme = themeAlpine;

  columnDefs = computed(() => {
    const getTranslatedHeader = (key: string) => (this.translationsReady() ? this.translate.instant(key) : key);

    return [
      { headerName: getTranslatedHeader('FIELDS.IDENTIFIER'), field: 'id', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('INFO.NAME'), field: `name.${this.lang()}`, sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      }
    ] as ColDef<ByProductItem>[];
  });

  constructor() {
    super();
    this.init();
  }

  async init() {
    this.isLoading.set(true);

    try {
      const byproducts = (await this.productsService.getByProducts())?.data || [];
      this.allByproducts.set(byproducts);
      this.displayByproducts.set(byproducts);

      this.initFuse();
      this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    } catch (error) {
      console.error('Failed to load slaughterhouses', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  initFuse() {
    this.fuse = new Fuse<ByProductItem>(this.allByproducts(), {
      keys: ['id', `name.${this.lang()}`, 'fillingSpots.id', `byProducts.name.${this.lang()}`, `byProducts.productType.name.${this.lang()}`],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) {
      return;
    }

    if (!term || term.length === 0) {
      this.displayByproducts.set(this.allByproducts());
      return;
    }

    const results = this.fuse.search(term);
    this.displayByproducts.set(results.map(r => r.item));
  }

  onRowClicked(event: RowClickedEvent) {
    this.router.navigate([event.data.id], { relativeTo: this.activatedRoute });
  }

  onGridReady(params: { api: GridApi<ByProductItem> }) {
    this.gridApi.set(params.api);
  }

  async exportToExcel() {
    const api = this.gridApi();
    if (!api) return;

    await this.exportGridToExcel(api, this.columnDefs(), 'byproduct-pricing', this.isExporting);
  }

  ngOnDestroy() {
    this.searchTermSubscription?.unsubscribe();
  }
}
